// Internet Section component for UtilitySelector

import type { InternetPlan, SelectedService } from '@/types'
import {
  getScoreColor,
  formatSpeed,
  getPercentileBadgeText,
} from '@/utils/planCalculations'

interface InternetSectionProps {
  internetPlans: InternetPlan[]
  selectedInternet?: SelectedService
  isExpanded: boolean
  onToggle: () => void
  onSelectPlan: (plan: InternetPlan) => void
  onShowDetails: (plan: InternetPlan) => void
}

export function InternetSection({
  internetPlans,
  selectedInternet,
  isExpanded,
  onToggle,
  onSelectPlan,
  onShowDetails,
}: InternetSectionProps) {
  return (
    <div
      className={`utility-selector ${isExpanded ? 'utility-selector--expanded' : ''} ${selectedInternet ? 'utility-selector--selected' : ''}`}
      data-utility="internet"
    >
      <div className="utility-selector__header" onClick={onToggle}>
        <div className="utility-selector__title-group">
          <div className="utility-selector__title">HOME INTERNET</div>
          <div className="utility-selector__description">
            {selectedInternet
              ? selectedInternet.vendorName
              : 'Select a plan to set up internet service'}
          </div>
        </div>
        <div className={`utility-selector__badge ${selectedInternet ? 'utility-selector__badge--added' : 'utility-selector__badge--none'}`}>
          {selectedInternet ? 'ADDED' : 'NONE'}
        </div>
      </div>
      <div className="utility-selector__content">
        {internetPlans.length === 0 ? (
          <div className="plan-card plan-card--loading">
            <div className="plan-card__body">
              <p>Loading internet plans...</p>
            </div>
          </div>
        ) : (
          <div className="plan-grid">
            {internetPlans.map(plan => {
              const planId = plan.id || plan.ID
              const scoreColor = getScoreColor(plan.percentileScore)
              const isSelected = selectedInternet?.planId === planId

              return (
                <div
                  key={planId}
                  className={`plan-card${isSelected ? ' plan-card--selected' : ''}`}
                  data-provider={plan.vendorName}
                >
                  <div className="plan-card__header">
                    <div className="plan-card__title">{plan.vendorName}</div>
                    {plan.hasFccData && plan.percentileScore && (
                      <span className="plan-card__percentile-badge">
                        {getPercentileBadgeText(plan.percentileScore)}
                      </span>
                    )}
                  </div>
                  <div className="plan-card__logo">
                    {plan.logo && <img src={plan.logo} alt={plan.vendorName} />}
                  </div>
                  <div className="plan-card__body">
                    {plan.price && (
                      <div className="plan-card__row">
                        <div className="plan-card__label">Price*</div>
                        <div className="plan-card__value">Starting at ${Number(plan.price).toFixed(2)}/mo</div>
                      </div>
                    )}
                    <div className="plan-card__row">
                      <div className="plan-card__label">Download Speed</div>
                      <div className="plan-card__value">
                        <span className="plan-card__row-dot" style={{ background: scoreColor }}></span>
                        {formatSpeed(plan.downloadSpeed)}
                      </div>
                    </div>
                    <div className="plan-card__row">
                      <div className="plan-card__label">Upload Speed</div>
                      <div className="plan-card__value">
                        <span className="plan-card__row-dot" style={{ background: scoreColor }}></span>
                        {formatSpeed(plan.uploadSpeed)}
                      </div>
                    </div>
                    {plan.hasFccData && plan.percentileScore != null && (
                      <div className="plan-card__row">
                        <div className="plan-card__label">Speed Score</div>
                        <div className="plan-card__score-display">
                          <span className="plan-card__row-dot" style={{ background: scoreColor }}></span>
                          <span className="plan-card__score-value">{plan.percentileScore.toFixed(1)}</span>
                          <span className="plan-card__score-max">/100</span>
                        </div>
                      </div>
                    )}
                    {plan.technology && (
                      <div className="plan-card__row">
                        <div className="plan-card__label">Type</div>
                        <div className="plan-card__value">{plan.technology}</div>
                      </div>
                    )}
                  </div>
                  <div className="plan-card__footer">
                    <button
                      className="plan-card__button js-select-utility"
                      onClick={() => onSelectPlan(plan)}
                    >
                      {isSelected ? 'SELECTED' : 'SELECT PLAN'}
                    </button>
                    {plan.hasFccData && (
                      <button
                        type="button"
                        className="plan-card__details"
                        onClick={(e) => {
                          e.preventDefault()
                          onShowDetails(plan)
                        }}
                      >
                        more details
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
        <div className="utility-selector__footer">
          <div className="utility-selector__footer-text">Concierge service provided by 2TurnItOn</div>
        </div>
      </div>
    </div>
  )
}
